import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../services/api";
import axios from "axios";
import API_URL from "../../../config/config";
import useToggleSidebar from "../../../components/togglesidebar";
import Sidebar from "../../sidebar";
import Header from "../../Header";
import Footer from "../../footer";

const STATUS_OPTIONS = ["new", "read", "closed"];
const API_FALLBACK_BASE = API_URL.replace(/\/admin$/, "");
const MISSING_ROUTE_ERROR =
  "Contact inquiry APIs are missing on backend (404). Deploy latest backend routes or set correct API host.";

function getAuthHeaders() {
  const token = localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function getInquiriesWithFallback(params) {
  try {
    return await api.get("/contact-inquiries", { params });
  } catch (err) {
    if (err?.response?.status !== 404) throw err;
    try {
      return await axios.get(`${API_FALLBACK_BASE}/contact-inquiries`, {
        params,
        withCredentials: true,
        timeout: 20000,
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });
    } catch (fallbackErr) {
      if (fallbackErr?.response?.status === 404) {
        const wrapped = new Error(MISSING_ROUTE_ERROR);
        wrapped.original = fallbackErr;
        throw wrapped;
      }
      throw fallbackErr;
    }
  }
}

async function updateInquiryStatusWithFallback(id, status) {
  try {
    return await api.patch(`/contact-inquiries/${id}/status`, { status });
  } catch (err) {
    if (err?.response?.status !== 404) throw err;
    try {
      return await axios.patch(
        `${API_FALLBACK_BASE}/contact-inquiries/${id}/status`,
        { status },
        {
          withCredentials: true,
          timeout: 20000,
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        }
      );
    } catch (fallbackErr) {
      if (fallbackErr?.response?.status === 404) {
        const wrapped = new Error(MISSING_ROUTE_ERROR);
        wrapped.original = fallbackErr;
        throw wrapped;
      }
      throw fallbackErr;
    }
  }
}

function ContactInquiries() {
  const { isSidebarOpen, toggleSidebar } = useToggleSidebar();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [updatingId, setUpdatingId] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getInquiriesWithFallback({
        page,
        limit,
        search: search.trim(),
        status: statusFilter,
      });

      if (response.data?.success) {
        setItems(response.data.items || []);
        setTotal(response.data.total || 0);
      } else {
        setItems([]);
        setTotal(0);
        setError(response.data?.message || "Failed to load inquiries.");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        navigate("/admin/login", { replace: true });
        return;
      }
      setItems([]);
      setTotal(0);
      setError(err?.response?.data?.message || err?.message || "Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleStatusUpdate = async (id, nextStatus) => {
    setUpdatingId(id);
    setError("");
    try {
      const response = await updateInquiryStatusWithFallback(id, nextStatus);
      if (response.data?.success) {
        setItems((prev) => prev.map((item) => (item._id === id ? { ...item, status: nextStatus } : item)));
        setSuccessMessage("Inquiry status updated successfully.");
        setTimeout(() => setSuccessMessage(""), 2500);
      } else {
        setError(response.data?.message || "Failed to update inquiry status.");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err?.response?.data?.message || err?.message || "Failed to update inquiry status.");
    } finally {
      setUpdatingId("");
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin | Contact Inquiries</title>
      </Helmet>
      <Sidebar isSidebarOpen={isSidebarOpen} />
      <div
        className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`}
        onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}
      >
        <div
          className="content-wrapper"
          style={{
            marginLeft: isSidebarOpen ? "250px" : "70px",
            transition: "margin-left .3s",
          }}
        >
          <Header toggleSidebar={toggleSidebar} />
          <section className="content">
            <div className="breadcrumb-main-bx">
              <div className="breadcrumb-bx">
                <Link className="breadcrumb-link" to="/admin/dashboard">
                  Dashboard
                </Link>
                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                <div className="breadcrumb-link breadcrumb-active">Contact Inquiries</div>
              </div>
            </div>

            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>Contact Inquiries</div>
                      <div className="d-flex gap-2 align-items-center">
                        <select
                          className="form-control"
                          value={statusFilter}
                          onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                          }}
                        >
                          <option value="all">All Statuses</option>
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="closed">Closed</option>
                        </select>
                        <input
                          type="search"
                          className="form-control"
                          placeholder="Search name/email/subject..."
                          value={search}
                          onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                          }}
                        />
                      </div>
                    </div>

                    <div className="card-body">
                      {successMessage && <div className="alert alert-success">{successMessage}</div>}
                      {error && <div className="alert alert-danger">{error}</div>}

                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>No.</th>
                              <th>Date</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Subject</th>
                              <th>Message</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan="7" className="text-center">
                                  Loading inquiries...
                                </td>
                              </tr>
                            ) : items.length > 0 ? (
                              items.map((item, index) => (
                                <tr key={item._id}>
                                  <td>{(page - 1) * limit + index + 1}</td>
                                  <td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}</td>
                                  <td>{item.name || "-"}</td>
                                  <td>{item.email ? <a href={`mailto:${item.email}?subject=${encodeURIComponent(`Re: ${item.subject || 'Inquiry'}`)}&body=${encodeURIComponent(`\n\nOriginal Message:\n${item.message || ''}`)}`} style={{ color: '#0d6efd', textDecoration: 'underline' }}>{item.email}</a> : "-"}</td>
                                  <td>{item.subject || "-"}</td>
                                  <td style={{ maxWidth: "340px", whiteSpace: "pre-wrap" }}>{item.message || "-"}</td>
                                  <td>
                                    <select
                                      className="form-control"
                                      value={item.status || "new"}
                                      disabled={updatingId === item._id}
                                      onChange={(e) => handleStatusUpdate(item._id, e.target.value)}
                                    >
                                      {STATUS_OPTIONS.map((status) => (
                                        <option key={status} value={status}>
                                          {status}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center">
                                  No contact inquiries found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          <label className="d-flex align-items-center gap-2">
                            <span>Show</span>
                            <select
                              className="form-control"
                              value={limit}
                              onChange={(e) => {
                                setLimit(Number(e.target.value));
                                setPage(1);
                              }}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                          </label>
                        </div>

                        <div>
                          <button
                            className="btn btn-outline-secondary btn-sm me-2"
                            disabled={page <= 1}
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                          >
                            Previous
                          </button>
                          <span>
                            Page {page} of {totalPages}
                          </span>
                          <button
                            className="btn btn-outline-secondary btn-sm ms-2"
                            disabled={page >= totalPages}
                            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </div>
    </>
  );
}

export default ContactInquiries;
